// LOTE CG-1 G3 — Tela "Encerradas": lista oportunidades Fechado / Perdido
// com filtros de período, responsável e busca; permite abrir e reabrir.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import Voltar from "@/components/Voltar";
import { brl, fmtDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, ArrowRight, RefreshCw } from "@/components/icons";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function monthBoundsSP(key) {
  // key = YYYY-MM. Devolve [inicio, fim) em ISO (UTC) do mês em America/Sao_Paulo.
  const [y, m] = key.split("-").map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1, 3, 0, 0));
  const end = new Date(Date.UTC(y, m, 1, 3, 0, 0));
  return [start.toISOString(), end.toISOString()];
}

function todayKeySP() {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" });
  return fmt.format(new Date()).slice(0, 7);
}

export default function PipelineEncerradas() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("ganhas");
  const [periodo, setPeriodo] = useState("mes"); // mes | trim | ano | custom
  const [custom, setCustom] = useState({ de: "", ate: "" });
  const [owner, setOwner] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [confirmReabrir, setConfirmReabrir] = useState(null); // {id, titulo}
  const [motivoReabrir, setMotivoReabrir] = useState("");
  const [busy, setBusy] = useState(false);

  const [de, ate] = useMemo(() => {
    const todayKey = todayKeySP();
    if (periodo === "mes") return monthBoundsSP(todayKey);
    if (periodo === "trim") {
      const [y, m] = todayKey.split("-").map(Number);
      const startM = m - ((m - 1) % 3);
      return [
        new Date(Date.UTC(y, startM - 1, 1, 3, 0, 0)).toISOString(),
        new Date(Date.UTC(y, startM + 2, 1, 3, 0, 0)).toISOString(),
      ];
    }
    if (periodo === "ano") {
      const [y] = todayKey.split("-").map(Number);
      return [
        new Date(Date.UTC(y, 0, 1, 3, 0, 0)).toISOString(),
        new Date(Date.UTC(y + 1, 0, 1, 3, 0, 0)).toISOString(),
      ];
    }
    if (custom.de && custom.ate) {
      return [new Date(custom.de + "T03:00:00Z").toISOString(), new Date(custom.ate + "T03:00:00Z").toISOString()];
    }
    return ["", ""];
  }, [periodo, custom]);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get("/deals");
      setData(r.data || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const encerradasBase = useMemo(() => {
    const stageFiltro = tab === "ganhas" ? "Fechado" : "Perdido";
    return (data || []).filter((d) => {
      if (d.stage !== stageFiltro) return false;
      const at = d.stage_updated_at || d.created_at || "";
      if (de && (at < de)) return false;
      if (ate && (at >= ate)) return false;
      if (owner && d.owner_name !== owner) return false;
      if (q && !`${d.titulo || ""} ${d.cliente_nome || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data, tab, de, ate, owner, q]);

  const owners = useMemo(
    () => [...new Set((data || []).filter((d) => d.stage === "Fechado" || d.stage === "Perdido").map((d) => d.owner_name).filter(Boolean))].sort(),
    [data],
  );

  const reabrir = async () => {
    if (!confirmReabrir?.id) return;
    const motivo = motivoReabrir.trim();
    if (motivo.length < 3) { toast.error("Informe o motivo (mínimo 3 caracteres)"); return; }
    setBusy(true);
    try {
      await api.post(`/deals/${confirmReabrir.id}/reabrir`, { motivo });
      toast.success("Oportunidade reaberta");
      setConfirmReabrir(null);
      setMotivoReabrir("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={load} />;

  return (
    <div className="pb-10">
      <Voltar fallback="/pipeline" label="Funil" className="mb-4" />
      <PageHeader title="Oportunidades encerradas" subtitle="Ganhas e perdidas por período" />
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="encerradas-tabs">
          <TabsTrigger value="ganhas" data-testid="tab-encerradas-ganhas">Ganhas</TabsTrigger>
          <TabsTrigger value="perdidas" data-testid="tab-encerradas-perdidas">Perdidas</TabsTrigger>
        </TabsList>

        <div className="flex flex-wrap items-end gap-2 my-4" data-testid="encerradas-filtros">
          <div className="flex flex-col">
            <span className="text-[11px] text-muted-foreground mb-1">Período</span>
            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="h-10 px-3 rounded-lg border border-input bg-white text-sm" data-testid="encerradas-periodo">
              <option value="mes">Mês atual</option>
              <option value="trim">Trimestre atual</option>
              <option value="ano">Ano atual</option>
              <option value="custom">Período…</option>
            </select>
          </div>
          {periodo === "custom" && (
            <div className="flex items-end gap-2">
              <div className="flex flex-col">
                <span className="text-[11px] text-muted-foreground mb-1">De</span>
                <input type="date" value={custom.de} onChange={(e) => setCustom((c) => ({ ...c, de: e.target.value }))} className="h-10 px-3 rounded-lg border border-input bg-white text-sm" data-testid="encerradas-de" />
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] text-muted-foreground mb-1">Até</span>
                <input type="date" value={custom.ate} onChange={(e) => setCustom((c) => ({ ...c, ate: e.target.value }))} className="h-10 px-3 rounded-lg border border-input bg-white text-sm" data-testid="encerradas-ate" />
              </div>
            </div>
          )}
          {owners.length > 1 && (
            <div className="flex flex-col">
              <span className="text-[11px] text-muted-foreground mb-1">Responsável</span>
              <select value={owner} onChange={(e) => setOwner(e.target.value)} className="h-10 px-3 rounded-lg border border-input bg-white text-sm" data-testid="encerradas-owner">
                <option value="">Todos</option>
                {owners.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          )}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título/cliente..." data-testid="encerradas-busca"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40" />
          </div>
          <div className="ml-auto text-xs text-muted-foreground self-end">{encerradasBase.length} resultado{encerradasBase.length === 1 ? "" : "s"}</div>
        </div>

        <TabsContent value="ganhas">
          <ListaEncerradas items={encerradasBase} tab="ganhas" onAbrir={(id) => navigate(`/deal/${id}`)} onReabrir={(d) => setConfirmReabrir(d)} />
        </TabsContent>
        <TabsContent value="perdidas">
          <ListaEncerradas items={encerradasBase} tab="perdidas" onAbrir={(id) => navigate(`/deal/${id}`)} onReabrir={(d) => setConfirmReabrir(d)} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirmReabrir} onOpenChange={(v) => { if (!v) { setConfirmReabrir(null); setMotivoReabrir(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reabrir oportunidade</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">{confirmReabrir?.titulo}</p>
          <textarea
            value={motivoReabrir}
            onChange={(e) => setMotivoReabrir(e.target.value)}
            placeholder="Motivo (mínimo 3 caracteres)"
            data-testid="encerradas-reabrir-motivo"
            className="w-full min-h-24 p-3 rounded-lg border border-input bg-white text-sm"
          />
          <DialogFooter>
            <button onClick={() => setConfirmReabrir(null)} className="h-10 px-4 rounded-lg border border-input text-sm font-medium hover:bg-slate-50">Cancelar</button>
            <button onClick={reabrir} disabled={busy} data-testid="encerradas-reabrir-confirmar" className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
              Reabrir
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ListaEncerradas({ items, tab, onAbrir, onReabrir }) {
  if (items.length === 0) {
    return <EmptyState icon={Search} title="Nada encontrado" subtitle="Ajuste os filtros ou o período." />;
  }
  return (
    <div className="bg-card rounded-2xl border border-black/5 overflow-hidden">
      <table className="w-full hidden md:table">
        <thead>
          <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-black/5">
            <th className="px-5 py-3">Cliente</th>
            <th className="px-5 py-3">Oportunidade</th>
            <th className="px-5 py-3">Valor</th>
            <th className="px-5 py-3">Data</th>
            {tab === "perdidas" && <th className="px-5 py-3">Motivo</th>}
            <th className="px-5 py-3">Vendedor</th>
            <th className="px-5 py-3 w-0"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id} className="border-b border-black/5 last:border-0 hover:bg-slate-50" data-testid={`encerrada-row-${d.id}`}>
              <td className="px-5 py-3 text-sm text-navy">{d.cliente_nome || "—"}</td>
              <td className="px-5 py-3 text-sm text-slate-700">{d.titulo}</td>
              <td className="px-5 py-3 text-sm tabular-nums">{brl(d.stage === "Fechado" && d.valor_fechado ? d.valor_fechado : d.valor)}</td>
              <td className="px-5 py-3 text-sm text-slate-500">{fmtDate(d.stage_updated_at || d.created_at)}</td>
              {tab === "perdidas" && <td className="px-5 py-3 text-sm text-slate-600 max-w-[200px] truncate" title={d.motivo_perda || ""}>{d.motivo_perda || "—"}</td>}
              <td className="px-5 py-3 text-sm text-slate-600">{d.owner_name || "—"}</td>
              <td className="px-5 py-3 whitespace-nowrap text-right flex gap-2 justify-end">
                <button onClick={() => onAbrir(d.id)} data-testid={`encerrada-abrir-${d.id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
                  <ArrowRight className="w-3.5 h-3.5 inline" /> Abrir
                </button>
                <button onClick={() => onReabrir({ id: d.id, titulo: d.titulo })} data-testid={`encerrada-reabrir-${d.id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand hover:bg-brand hover:text-white">
                  <RefreshCw className="w-3.5 h-3.5 inline" /> Reabrir
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-black/5">
        {items.map((d) => (
          <div key={d.id} className="p-4" data-testid={`encerrada-card-${d.id}`}>
            <div className="text-sm font-semibold text-navy">{d.titulo}</div>
            <div className="text-xs text-muted-foreground">{d.cliente_nome || "—"} · {d.owner_name || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{fmtDate(d.stage_updated_at || d.created_at)} · {brl(d.stage === "Fechado" && d.valor_fechado ? d.valor_fechado : d.valor)}</div>
            {tab === "perdidas" && d.motivo_perda ? (
              <div className="text-xs text-slate-600 mt-1"><b>Motivo:</b> {d.motivo_perda}</div>
            ) : null}
            <div className="flex gap-2 mt-2">
              <button onClick={() => onAbrir(d.id)} data-testid={`encerrada-abrir-m-${d.id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                Abrir
              </button>
              <button onClick={() => onReabrir({ id: d.id, titulo: d.titulo })} data-testid={`encerrada-reabrir-m-${d.id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand">
                Reabrir
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
